import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RegistrationData } from '../../pages/RegisterPage';

interface RoleSelectionStepProps {
  data: RegistrationData;
  onDataChange: (data: Partial<RegistrationData>) => void;
  onNext: () => void;
}

const RoleSelectionStep: React.FC<RoleSelectionStepProps> = ({
  data,
  onDataChange,
  onNext,
}) => {
  const { t } = useTranslation();
  const handleRoleSelect = (role: 'CLIENT' | 'FREELANCER' | 'AGENCY') => {
    onDataChange({ role });
  };

  const canProceed = data.role !== null;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          mb: 4,
          fontWeight: 600,
          color: '#ffffff',
        }}
      >
        {t('auth.register.roleSelection.title')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          mb: 4,
          color: '#a0a0a0',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {t('auth.register.roleSelection.subtitle')}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Client Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: data.role === 'CLIENT' ? '2px solid #28A0F0' : '2px solid transparent',
              background: data.role === 'CLIENT' 
                ? 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                border: '2px solid #28A0F0',
              },
            }}
            onClick={() => handleRoleSelect('CLIENT')}
          >
            <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                }}
              >
                <BusinessIcon sx={{ fontSize: 40, color: '#ffffff' }} />
              </Avatar>
              
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                {t('auth.register.roleSelection.client')}
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#a0a0a0',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                {t('auth.register.roleSelection.clientDescription')}
              </Typography>

              <Box sx={{ textAlign: 'left', flex: 1 }}>
                {(() => {
                  const features = t('auth.register.roleSelection.clientFeatures', { returnObjects: true });
                  if (Array.isArray(features)) {
                    return features.map((feature: any, index: number) => (
                      <Typography key={index} variant="body2" sx={{ color: '#d0d0d0', mb: 1 }}>
                        ✓ {String(feature)}
                      </Typography>
                    ));
                  }
                  // Fallback if translation is not loaded
                  return [
                    'Публикувайте проекти',
                    'Получавайте оферти', 
                    'Избирайте най-добрите изпълнители',
                    'Управлявайте проектите си'
                  ].map((feature: string, index: number) => (
                    <Typography key={index} variant="body2" sx={{ color: '#d0d0d0', mb: 1 }}>
                      ✓ {feature}
                    </Typography>
                  ));
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Provider Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: data.role === 'FREELANCER' ? '2px solid #28A0F0' : '2px solid transparent',
              background: data.role === 'FREELANCER' 
                ? 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                border: '2px solid #28A0F0',
              },
            }}
            onClick={() => handleRoleSelect('FREELANCER')}
          >
            <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  background: 'linear-gradient(135deg, #ec4899 0%, #28A0F0 100%)',
                }}
              >
                <WorkIcon sx={{ fontSize: 40, color: '#ffffff' }} />
              </Avatar>
              
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                {t('auth.register.roleSelection.provider')}
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#a0a0a0',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                {t('auth.register.roleSelection.providerDescription')}
              </Typography>

              <Box sx={{ textAlign: 'left', flex: 1 }}>
                {(() => {
                  const features = t('auth.register.roleSelection.providerFeatures', { returnObjects: true });
                  if (Array.isArray(features)) {
                    return features.map((feature: any, index: number) => (
                      <Typography key={index} variant="body2" sx={{ color: '#d0d0d0', mb: 1 }}>
                        ✓ {String(feature)}
                      </Typography>
                    ));
                  }
                  // Fallback if translation is not loaded
                  return [
                    'Разглеждайте проекти',
                    'Подавайте оферти',
                    'Изграждайте портфолио', 
                    'Печелете пари'
                  ].map((feature: string, index: number) => (
                    <Typography key={index} variant="body2" sx={{ color: '#d0d0d0', mb: 1 }}>
                      ✓ {feature}
                    </Typography>
                  ));
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          disabled={!canProceed}
          onClick={onNext}
          sx={{
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            px: 6,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
            },
            '&:disabled': {
              background: '#2d2d44',
              color: '#6b7280',
            },
          }}
        >
          {t('auth.register.roleSelection.continue')}
        </Button>
      </Box>
    </Box>
  );
};

export default RoleSelectionStep;
